import React from 'react';

export const TermsEn: React.FC = () => (
  <>
    <section>
      <h2>1. Who we are and what these terms cover</h2>
      <p>
        The Roadbase platform, available at roadbase.eu, is operated by Leonardo Ferreira, a sole
        trader, Portuguese tax number (NIF) 305516760, of Praça Nove de Abril, 123, 4200-422 Porto,
        Portugal.
      </p>
      <p>
        These terms govern access to and use of the platform. By creating an account or submitting a
        quote request, you agree to be bound by them. If you do not agree, do not use the service.
      </p>
      <p>
        The platform is intended exclusively for businesses and professionals. It is not intended
        for consumers or for anyone under 18.
      </p>
    </section>

    <section>
      <h2>2. What Roadbase is — and is not</h2>
      <p>
        Roadbase is a platform that intermediates quote requests for road transport of out-of-gauge
        (OOG) and special cargo. We receive your request, contact carriers in our network, and
        present their responses in a comparable format.
      </p>
      <p>This clause is essential and prevails over any understanding to the contrary:</p>
      <blockquote>
        <p>
          Roadbase is not a carrier, not a freight forwarder, and not an agent of either party. We
          do not perform, arrange or supervise transport. We are not a party to the transport
          contract, we do not act as consignor or as carrier, and we give no warranty as to the
          performance, timing, integrity of the cargo, or regulatory compliance of any transport.
        </p>
        <p>
          The transport contract is entered into directly and exclusively between the client and the
          carrier, and is governed by the terms agreed between them and by the CMR Convention where
          applicable.
        </p>
      </blockquote>
    </section>

    <section>
      <h2>3. Nature of the quotes</h2>
      <p>
        Quotes shown on the platform are indicative. They reflect information supplied by carriers
        on the basis of the data provided by the client, and do not constitute a binding offer from
        Roadbase.
      </p>
      <p>
        Final price, transit time, liability terms, insurance, exceptional transport permits and
        escort costs remain subject to the carrier's confirmation and to its own general
        conditions. Changes to the cargo, route or dates may change the price.
      </p>
      <p>
        Roadbase does not independently verify the accuracy of quotes, the standing of carriers,
        the validity of their licences or the scope of their insurance. Clients are advised to
        carry out whatever checks their own due diligence requires before contracting.
      </p>
    </section>

    <section>
      <h2>4. Accounts</h2>
      <p>
        To track requests, the client creates an account linked to an email address. The client is
        responsible for the accuracy of the data provided, for keeping credentials confidential,
        and for all activity carried out through the account. Any unauthorised use must be reported
        to us immediately.
      </p>
      <p>
        We may suspend or close accounts that breach these terms, that contain manifestly false
        data, or that are used in a way that harms the service or third parties.
      </p>
    </section>

    <section>
      <h2>5. Client obligations regarding cargo data</h2>
      <p>This clause carries particular operational weight in OOG transport.</p>
      <p>
        The client undertakes to provide accurate dimensions, weight, nature and handling
        requirements of the cargo, and to state whether the cargo is classified as dangerous goods
        (ADR) or requires special conditions.
      </p>
      <p>
        Incorrect data may result in refusal of the cargo at loading, invalidation of exceptional
        transport permits, unsuitable equipment being assigned, additional costs, delays and safety
        risks. Responsibility for incorrect or incomplete data lies solely with the client,
        including as towards the carrier.
      </p>
    </section>

    <section>
      <h2>6. Price</h2>
      <p>
        During the beta period, use of the platform is free of charge. We will give reasonable
        notice, and in any event no less than 30 days, before moving to a paid model, leaving the
        client free not to subscribe and to close the account.
      </p>
    </section>

    <section>
      <h2>7. Acceptable use</h2>
      <p>The client undertakes not to:</p>
      <ul>
        <li>
          extract, copy, reproduce or reuse, by any means, the Roadbase carrier directory or any
          substantial part of it, including by automated collection (scraping); this database is
          protected under database rights;
        </li>
        <li>
          submit fictitious or mass-duplicated requests, or requests intended to probe the carrier
          network without genuine intention to contract;
        </li>
        <li>upload files containing malicious code, or content over which the client holds no rights;</li>
        <li>access or attempt to access areas, accounts or data belonging to others;</li>
        <li>
          use the platform for unlawful purposes or in breach of applicable sanctions, embargoes or
          export control legislation.
        </li>
      </ul>
    </section>

    <section>
      <h2>8. Intellectual property</h2>
      <p>
        The platform, its code, interface, texts, brand and carrier directory are owned by or
        licensed to Leonardo Ferreira. Access to the service grants no licence over these elements
        beyond what is strictly necessary for normal use.
      </p>
      <p>
        Data submitted by the client remains the client's. The client grants us only the licence
        needed to process it for the purpose of providing the service.
      </p>
    </section>

    <section>
      <h2>9. Availability</h2>
      <p>
        We aim to keep the platform continuously available but do not guarantee uninterrupted
        service. Downtime may occur for maintenance, updates or fault correction. During the beta
        period there is no service level commitment (SLA), and features may be changed or removed.
      </p>
    </section>

    <section>
      <h2>10. Limitation of liability</h2>
      <p>To the fullest extent permitted by applicable law, Roadbase is not liable for:</p>
      <ul>
        <li>acts, omissions, breaches, delays, damage or loss attributable to carriers or any third party;</li>
        <li>damage to cargo, delivery delays, demurrage costs, fines or losses arising from transport;</li>
        <li>commercial decisions taken by the client on the basis of indicative quotes;</li>
        <li>loss of profit, loss of opportunity, indirect or consequential damages;</li>
        <li>
          temporary unavailability of the platform or loss of data not attributable to our wilful
          misconduct or gross negligence.
        </li>
      </ul>
      <p>
        Nothing in these terms excludes liability that the law does not permit to be excluded, in
        particular for wilful misconduct or for death and personal injury.
      </p>
    </section>

    <section>
      <h2>11. Personal data</h2>
      <p>
        The processing of personal data is governed by our{' '}
        <a href="/en/privacy">Privacy Policy</a>, which forms an integral part of these terms.
      </p>
    </section>

    <section>
      <h2>12. Duration and termination</h2>
      <p>
        The client may close the account at any time by writing to{' '}
        <a href="mailto:info@roadbase.eu">info@roadbase.eu</a>. We may discontinue the service, in
        whole or in part, on reasonable notice, except in the event of a breach of these terms,
        where termination may be immediate.
      </p>
      <p>
        Termination does not affect rights and obligations accrued up to that date, nor requests and
        transport contracts already under way between the client and carriers.
      </p>
    </section>

    <section>
      <h2>13. Changes to these terms</h2>
      <p>
        We may amend these terms. Changes with material impact are communicated by email at least 15
        days in advance. Continued use of the platform after they take effect constitutes
        acceptance.
      </p>
    </section>

    <section>
      <h2>14. Governing law and jurisdiction</h2>
      <p>
        These terms are governed by Portuguese law. Any dispute shall be submitted to the courts of
        Porto, Portugal, to the exclusion of any other forum.
      </p>
    </section>

    <section>
      <h2>15. Contact</h2>
      <p>
        <strong>Leonardo Ferreira</strong> — <a href="mailto:info@roadbase.eu">info@roadbase.eu</a>
      </p>
    </section>
  </>
);
